import { motion } from "framer-motion";
import { Stethoscope, MessagesSquare, FolderLock, ArrowUpRight } from "lucide-react";
import { clinicConfig } from "@/config/clinic";
import { fadeUp, stagger, viewport } from "@/lib/reveal";

const CARDS = [
  {
    num: "01",
    icon: Stethoscope,
    title: "In-Person Primary Care",
    body: "Routine visits, chronic condition management, and ongoing family medicine — delivered face to face, right here in Keystone.",
    points: ["Annual physicals & screenings", "Chronic care & follow-ups", "Whole-family primary care"],
    testid: "care-card-in-person",
  },
  {
    num: "02",
    icon: MessagesSquare,
    title: "Virtual Care with Doc Talk",
    body: "Ask health questions any time and get answers from a licensed clinician within 24 hours through secure, message-based care.",
    points: ["Anytime health questions", "Replies within 24 hours", "Secure clinician messaging"],
    testid: "care-card-virtual",
  },
  {
    num: "03",
    icon: FolderLock,
    title: "Patient Access",
    body: "Sign in to connect digitally, use AI chat for guidance, and access your health records from wherever you are.",
    points: ["AI chat support", "Your health records", "One secure sign-in"],
    testid: "care-card-access",
  },
];

export const CareOptions = ({ onRequest }) => {
  return (
    <section id="care" data-testid="care-section" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="max-w-2xl"
        >
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-accent">
            Your options for care
          </p>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-foreground md:text-5xl">
            Two clear ways to be seen — and one place to sign in.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            Whether you prefer to visit the office in Keystone or connect from
            home, your next step is simple and close by.
          </p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.num}
                variants={fadeUp}
                data-testid={c.testid}
                className="group relative flex flex-col border border-border bg-card p-8 transition-colors hover:border-accent"
              >
                <div className="flex items-start justify-between">
                  <span className="font-serif text-5xl font-medium text-foreground/15 transition-colors group-hover:text-accent/40">
                    {c.num}
                  </span>
                  <Icon className="h-8 w-8 text-primary dark:text-accent" strokeWidth={1.5} />
                </div>
                <h3 className="mt-8 font-serif text-2xl font-medium tracking-tight text-foreground">
                  {c.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {c.body}
                </p>
                <ul className="mt-6 space-y-2.5 border-t border-border pt-6">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm text-foreground/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
        >
          <a
            href={clinicConfig.patientPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="care-signin-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            Sign in to Doc Talk
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </a>
          <button
            onClick={onRequest}
            data-testid="care-request-cta"
            className="inline-flex items-center gap-2 text-base font-semibold text-foreground underline decoration-accent decoration-2 underline-offset-4 transition-colors hover:text-primary"
          >
            Request an appointment
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CareOptions;
